import React, { useEffect } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Minus, Plus, Trash2, ShoppingBag } from 'lucide-react';
import { cartItemKey, useCartStore } from '@/lib/store';
import { useAdminStore } from '@/lib/admin-store';
import { useTranslation, useCurrency } from '@/hooks/useTranslation';

const CartDrawer: React.FC = () => {
  const navigate = useNavigate();
  const { items, isOpen, toggleCart, removeItem, updateQuantity, subtotal } =
    useCartStore();
  const allArtworks = useAdminStore((s) => s.artworks);
  const { t, localize } = useTranslation();
  const { formatPrice } = useCurrency();

  useEffect(() => { if (isOpen) void useCartStore.getState().hydrateImages().then(changed => { if (changed) toast('Your cart was updated. Please review prices and availability.'); }).catch(() => toast.error('Could not refresh the cart. Please retry.')); }, [isOpen]);

  const getMaxStock = (artworkId: string): number => {
    const art = allArtworks.find((a) => a._id === artworkId);
    if (!art) return 99;
    if (art.rarity === 'unique') return 1;
    if (art.edition) return Math.max(0, art.edition.total - art.edition.current + 1);
    return 99;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[900] bg-black/20 backdrop-blur-sm"
            onClick={toggleCart}
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.5, ease: [0.76, 0, 0.24, 1] }}
            className="fixed top-0 right-0 h-full w-full max-w-md z-[950] bg-white dark:bg-black border-l border-black/5 dark:border-white/5 flex flex-col transition-colors duration-700"
          >
            {/* Header */}
            <div className="p-8 flex justify-between items-center border-b border-black/5 dark:border-white/5">
              <div className="flex items-center gap-3">
                <ShoppingBag size={20} />
                <h2 className="text-lg font-syncopate font-bold uppercase tracking-tighter">
                  {t.cart.title}
                </h2>
                <span className="text-[9px] px-2 py-0.5 bg-black/5 dark:bg-white/5 rounded-full font-bold">
                  {items.length}
                </span>
              </div>
              <button
                onClick={toggleCart}
                className="w-10 h-10 rounded-full bg-black/5 dark:bg-white/5 flex items-center justify-center hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-all"
              >
                <X size={18} />
              </button>
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center space-y-4">
                  <ShoppingBag size={40} className="text-black/10 dark:text-white/10" />
                  <p className="text-[10px] font-bold uppercase tracking-widest text-black/30 dark:text-white/30">
                    {t.cart.empty}
                  </p>
                </div>
              ) : (
                items.map((item) => (
                  <div
                    key={cartItemKey(item)}
                    className="flex gap-4 p-4 bg-black/[0.02] dark:bg-white/[0.02] border border-black/5 dark:border-white/5 rounded-2xl"
                  >
                    {item.artwork.images?.[0] ? (
                      <img
                        src={item.artwork.images[0]}
                        alt={localize(item.artwork.title)}
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-lg bg-black/5 dark:bg-white/5 flex items-center justify-center">
                        <ShoppingBag size={20} className="text-black/20 dark:text-white/20" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h4 className="text-xs font-bold uppercase tracking-tight truncate">
                        {localize(item.artwork.title)}{item.variantId && <> · {localize(item.artwork.sizeVariants?.find(v => v._id === item.variantId)?.label || {en:"",ka:""})}</>}{item.unavailable && <span role="alert"> — Unavailable</span>}
                      </h4>
                      <p className="text-[9px] text-black/40 dark:text-white/40 uppercase tracking-wider mt-1">
                        {item.frame === 'unframed'
                          ? t.artwork.unframed
                          : `${item.frame} frame`}
                      </p>
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() =>
                              updateQuantity(cartItemKey(item), item.quantity - 1)
                            }
                            className="w-6 h-6 rounded-full bg-black/5 dark:bg-white/5 flex items-center justify-center hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-all"
                          >
                            <Minus size={10} />
                          </button>
                          <span className="text-xs font-bold min-w-[1.5ch] text-center">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() =>
                              updateQuantity(cartItemKey(item), item.quantity + 1)
                            }
                            disabled={item.quantity >= getMaxStock(item.artwork._id)}
                            className="w-6 h-6 rounded-full bg-black/5 dark:bg-white/5 flex items-center justify-center hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-all disabled:opacity-30 disabled:pointer-events-none"
                          >
                            <Plus size={10} />
                          </button>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-bold font-syncopate">
                            {formatPrice(item.totalPrice, item.artwork.price.currency)}
                          </span>
                          <button
                            onClick={() => removeItem(cartItemKey(item))}
                            className="text-black/20 dark:text-white/20 hover:text-red-500 transition-colors"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            {items.length > 0 && (
              <div className="p-8 border-t border-black/5 dark:border-white/5 space-y-6">
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-black/40 dark:text-white/40">
                    {t.cart.subtotal}
                  </span>
                  <span className="text-2xl font-syncopate font-bold">
                    {formatPrice(subtotal(), items[0]?.artwork.price.currency)}
                  </span>
                </div>
                <button
                  onClick={() => {
                    toggleCart();
                    navigate('/checkout');
                  }}
                  className="w-full py-5 bg-black dark:bg-white text-white dark:text-black text-xs font-bold uppercase tracking-[0.3em] rounded-2xl hover:opacity-90 active:scale-95 transition-all"
                >
                  {t.cart.checkout}
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
